import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { Loader2, Save, ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { CATEGORIES, STATUSES, OUTREACH_STAGES } from "./constants";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AdminCrmContactCreatePage() {
  const navigate = useNavigate();
  const [draft, setDraft] = useState({
    name: "", email: "", phone: "", title: "", organization: "",
    city: "", country: "", linkedin_url: "", website: "",
    category: "Other", status: "New", outreach_stage: "NotStarted",
    lead_score: 30, priority_score: 30, source: "manual",
    notes_summary: "",
  });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setDraft((p) => ({ ...p, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = { ...draft, lead_score: Number(draft.lead_score) || 0, priority_score: Number(draft.priority_score) || 0 };
      Object.keys(payload).forEach((k) => { if (payload[k] === "") payload[k] = null; });
      const { data } = await axios.post(`${API}/admin/crm/contacts`, payload);
      toast.success("Contact created");
      navigate(`/admin/crm/contacts/${data.id}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Create failed");
    } finally { setBusy(false); }
  };

  const F = ({ label, k, type = "text", required, options, multiline }) => (
    <label className="block">
      <span className="text-xs font-semibold text-slate-600 uppercase tracking-wider">{label}{required && " *"}</span>
      {options ? (
        <select required={required} className="mt-1 w-full text-sm rounded-md border border-slate-300 px-3 py-2 bg-white" value={draft[k]} onChange={(e) => set(k, e.target.value)} data-testid={`crm-create-${k}`}>
          {options.map((o) => <option key={o}>{o}</option>)}
        </select>
      ) : multiline ? (
        <textarea rows={3} className="mt-1 w-full text-sm rounded-md border border-slate-300 px-3 py-2" value={draft[k] || ""} onChange={(e) => set(k, e.target.value)} data-testid={`crm-create-${k}`} />
      ) : (
        <input type={type} required={required} className="mt-1 w-full text-sm rounded-md border border-slate-300 px-3 py-2" value={draft[k] || ""} onChange={(e) => set(k, e.target.value)} data-testid={`crm-create-${k}`} />
      )}
    </label>
  );

  return (
    <div className="space-y-5 max-w-4xl" data-testid="crm-create-page">
      <Link to="/admin/crm/contacts" className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-800">
        <ArrowLeft className="w-4 h-4" /> All contacts
      </Link>
      <h1 className="text-2xl font-bold text-slate-900 font-['Plus_Jakarta_Sans']">New contact</h1>
      <form onSubmit={submit} className="bg-white border border-slate-200 rounded-2xl p-6 grid sm:grid-cols-2 gap-4">
        <F label="Name" k="name" required />
        <F label="Email" k="email" type="email" />
        <F label="Phone" k="phone" />
        <F label="Title" k="title" />
        <F label="Organization" k="organization" />
        <F label="Category" k="category" options={CATEGORIES} />
        <F label="Status" k="status" options={STATUSES} />
        <F label="Outreach stage" k="outreach_stage" options={OUTREACH_STAGES} />
        <F label="City" k="city" />
        <F label="Country" k="country" />
        <F label="LinkedIn URL" k="linkedin_url" />
        <F label="Website" k="website" />
        <F label="Lead score" k="lead_score" type="number" />
        <F label="Priority score" k="priority_score" type="number" />
        <div className="sm:col-span-2"><F label="Notes / summary" k="notes_summary" multiline /></div>
        <div className="sm:col-span-2 flex justify-end gap-2">
          <Link to="/admin/crm/contacts"><Button variant="outline" size="sm" type="button">Cancel</Button></Link>
          <Button size="sm" type="submit" disabled={busy} className="bg-cyan-700 hover:bg-cyan-800 text-white" data-testid="crm-create-submit">
            {busy ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Save className="w-4 h-4 mr-1.5" />}
            Create
          </Button>
        </div>
      </form>
    </div>
  );
}
